import axios, { AxiosError, type AxiosRequestHeaders } from "axios";

import { refreshToken } from "@/api/auth";
import { AuthState } from "@/utils/auth-state";
import { calculateMaxAge } from "@/utils/calculate-max-age";
import { SessionAuthCookies } from "../cookies";
import { env } from "../env";

/**
 * Clears the authentication session locally.
 * Does NOT call logout API to avoid 401 cascade when token is already invalid.
 */
function clearAuthSession() {
  SessionAuthCookies.remove();
  SessionAuthCookies.removeRefreshToken();
  // Only update AuthState.accessToken - the request interceptor reads from this
  AuthState.accessToken = null;
}

function dispatchUnauthorizedRedirect() {
  // Dispatch event to trigger navigation in SessionProvider
  // Using React Router navigation instead of window.location.href for better SPA behavior
  window.dispatchEvent(new CustomEvent("auth:unauthorized"));
}

/**
 * Performs token refresh with shared promise coordination.
 * This ensures only one refresh happens at a time across both
 * SessionProvider and axios interceptor.
 */
export async function performTokenRefresh(refreshTokenValue: string): Promise<string | null> {
  // If a refresh is already in progress, return the existing promise
  if (AuthState.refreshPromise) {
    return AuthState.refreshPromise;
  }

  // Create and store the refresh promise
  const promise = (async () => {
    try {
      AuthState.isRefreshing = true;
      const response = await refreshToken(refreshTokenValue);
      const newAccessToken = response.data.access_token;
      const newRefreshToken = response.data.refresh_token;
      const refreshExpiresIn = response.data.refresh_expires_in;

      // Only update AuthState.accessToken - the request interceptor reads from this
      AuthState.accessToken = newAccessToken;

      if (newRefreshToken) {
        const maxAge = calculateMaxAge(new Date(Date.now() + refreshExpiresIn * 1000));
        SessionAuthCookies.setRefreshToken(newRefreshToken, { maxAge });
      }

      return newAccessToken;
    } catch (refreshError) {
      // If refresh token API returns 401, session is invalid - clear and redirect
      const isRefresh401 =
        refreshError instanceof AxiosError && refreshError.response?.status === 401;

      if (isRefresh401) {
        // Clear session and redirect - use consistent error message
        AuthState.isLoggingOut = true;
        clearAuthSession();
        dispatchUnauthorizedRedirect();
        return Promise.reject(new Error("REFRESH_TOKEN_INVALID"));
      }

      // For other errors (network errors, 500, etc.), also logout and redirect
      console.error("Token refresh failed:", refreshError);
      AuthState.isLoggingOut = true;
      clearAuthSession();
      dispatchUnauthorizedRedirect();
      return Promise.reject(new Error("REFRESH_TOKEN_FAILED"));
    } finally {
      AuthState.isRefreshing = false;
      AuthState.refreshPromise = null;
      // Reset logout flag after a delay to allow redirect to complete
      setTimeout(() => {
        AuthState.isLoggingOut = false;
      }, 1000);
    }
  })();

  AuthState.refreshPromise = promise;
  return promise;
}

export const httpClient = axios.create({
  baseURL: env.VITE_BASE_API_URL,
});

// Request interceptor: Always use the latest token from in-memory auth state
httpClient.interceptors.request.use(
  (config) => {
    const token = AuthState.accessToken;

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    } else {
      // Explicitly remove Authorization header if no token
      delete config.headers.Authorization;
    }

    return config;
  },
  (error) => Promise.reject(error instanceof Error ? error : new Error(String(error)))
);

// Response interceptor: Handle 401 errors
httpClient.interceptors.response.use(
  (response) => {
    // Reset logout flag on successful response
    AuthState.isLoggingOut = false;
    return response;
  },
  async (error: AxiosError) => {
    // Check if the error is due to an unauthorized request (401)
    const status = error.response?.status;
    const refreshTokenValue = SessionAuthCookies.getRefreshToken();
    const originalRequest = error.config;
    const isRefreshRequest = originalRequest?.url?.includes("/auth/refresh");

    // Attempt token refresh when applicable
    // Uses shared promise pattern to prevent duplicate refreshes between
    // axios interceptor and SessionProvider
    if (
      status === 401 &&
      !isRefreshRequest &&
      !AuthState.isLoggingOut &&
      !AuthState.isLoggingIn &&
      refreshTokenValue
    ) {
      try {
        // performTokenRefresh handles shared promise coordination internally
        const newToken = await performTokenRefresh(refreshTokenValue);

        if (newToken && originalRequest) {
          if (originalRequest.headers?.set) {
            originalRequest.headers.set("Authorization", `Bearer ${newToken}`);
          } else {
            const headers =
              (originalRequest.headers as AxiosRequestHeaders | undefined) ??
              ({} as AxiosRequestHeaders);
            headers.Authorization = `Bearer ${newToken}`;
            originalRequest.headers = headers;
          }

          return httpClient(originalRequest);
        }
      } catch (refreshError) {
        // Refresh failed - session already cleared in performTokenRefresh, just reject
        const errorToReject =
          refreshError instanceof Error ? refreshError : new Error(String(refreshError));
        return Promise.reject(errorToReject);
      }
    }

    // Fallback: clear session and redirect when refresh is not possible
    // This handles cases where there's no refresh token or we're in a state where refresh shouldn't happen
    if (
      status === 401 &&
      !isRefreshRequest &&
      !AuthState.isLoggingOut &&
      !AuthState.isLoggingIn &&
      !AuthState.isRefreshing &&
      !refreshTokenValue
    ) {
      AuthState.isLoggingOut = true;
      clearAuthSession();
      dispatchUnauthorizedRedirect();

      setTimeout(() => {
        AuthState.isLoggingOut = false;
      }, 1000);
    }

    // Handle 401 from refresh token request - this is handled in performTokenRefresh
    // but we keep this as a safety net in case the refresh is called directly
    if (status === 401 && isRefreshRequest && !AuthState.isLoggingOut) {
      AuthState.isLoggingOut = true;
      clearAuthSession();
      dispatchUnauthorizedRedirect();

      setTimeout(() => {
        AuthState.isLoggingOut = false;
      }, 1000);
    }

    return Promise.reject(error);
  }
);
